import React, { useState, useEffect } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Search, Plus, MoreVertical, Edit, Trash2, Briefcase, Users, MapPin,
  Building2, IndianRupee, Clock, RefreshCw, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Open': 'bg-green-100 text-green-700',
  'Closed': 'bg-red-100 text-red-700',
  'On Hold': 'bg-amber-100 text-amber-700'
};

const JobList = ({ onViewJob, getJobUrl }) => {
  const {
    jobs, loading, pipelineTemplates,
    fetchJobs, createJob, updateJob, deleteJob, fetchPipelineTemplates
  } = useRecruitment();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    employment_type: 'Full-time',
    experience_min: '',
    experience_max: '',
    salary_min: '',
    salary_max: '',
    description: '',
    requirements: '',
    status: 'Open',
    pipeline_template_id: ''
  });

  useEffect(() => {
    fetchJobs();
    fetchPipelineTemplates();
  }, []);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !search || 
      job.title.toLowerCase().includes(search.toLowerCase()) ||
      job.department?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const resetForm = () => {
    setFormData({
      title: '',
      department: '',
      location: '',
      employment_type: 'Full-time',
      experience_min: '',
      experience_max: '',
      salary_min: '',
      salary_max: '',
      description: '',
      requirements: '',
      status: 'Open',
      pipeline_template_id: ''
    });
    setIsEditing(false);
    setSelectedJob(null);
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Job title is required');
      return;
    }

    const data = {
      ...formData,
      experience_min: formData.experience_min ? parseInt(formData.experience_min) : null,
      experience_max: formData.experience_max ? parseInt(formData.experience_max) : null,
      salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
      salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
      pipeline_template_id: formData.pipeline_template_id || null
    };

    let result;
    if (isEditing && selectedJob) {
      result = await updateJob(selectedJob.id, data);
    } else {
      result = await createJob(data);
    }

    if (result.success) {
      toast.success(isEditing ? 'Job updated' : 'Job created');
      setShowDialog(false);
      resetForm();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = (job) => {
    setSelectedJob(job);
    setFormData({
      title: job.title || '',
      department: job.department || '',
      location: job.location || '',
      employment_type: job.employment_type || 'Full-time',
      experience_min: job.experience_min?.toString() || '',
      experience_max: job.experience_max?.toString() || '',
      salary_min: job.salary_min?.toString() || '',
      salary_max: job.salary_max?.toString() || '',
      description: job.description || '',
      requirements: job.requirements || '',
      status: job.status || 'Open',
      pipeline_template_id: job.pipeline_template_id || ''
    });
    setIsEditing(true);
    setShowDialog(true);
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Are you sure you want to delete "${job.title}"?`)) return;
    
    const result = await deleteJob(job.id);
    if (result.success) {
      toast.success('Job deleted');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Jobs</h2>
          <p className="text-slate-500">Manage job postings and positions</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-job-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Job
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by title or department..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="job-search"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="job-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={() => fetchJobs()} data-testid="refresh-jobs">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Job Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          <div className="col-span-full text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            No jobs found
          </div>
        ) : (
          filteredJobs.map((job) => (
            <Card 
              key={job.id} 
              className="hover:shadow-md transition-shadow cursor-pointer" 
              data-testid={`job-card-${job.id}`}
              onClick={() => onViewJob?.(job.id)}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">
                      <a
                        href={getJobUrl ? getJobUrl(job.id) : '#'}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                          e.preventDefault();
                          e.stopPropagation();
                          onViewJob?.(job.id);
                        }}
                        className="hover:underline hover:text-purple-600"
                        data-testid={`job-link-${job.id}`}
                      >
                        {job.title}
                      </a>
                    </CardTitle>
                    {job.department && (
                      <CardDescription className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {job.department}
                      </CardDescription>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`job-actions-${job.id}`} onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewJob?.(job.id); }}>
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(job); }}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(job); }} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Badge className={STATUS_COLORS[job.status] || 'bg-slate-100'}>{job.status}</Badge>
                    <Badge variant="outline">{job.employment_type}</Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {job.location && (
                      <div className="flex items-center gap-1 text-slate-600">
                        <MapPin className="w-3 h-3" /> {job.location}
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-slate-600">
                      <Users className="w-3 h-3" /> {job.candidate_count || 0} candidates
                    </div>
                    {(job.experience_min !== null || job.experience_max !== null) && (
                      <div className="flex items-center gap-1 text-slate-600 col-span-2">
                        <Clock className="w-3 h-3" />
                        {job.experience_min ?? 0} - {job.experience_max ?? '∞'} years
                      </div>
                    )}
                    {(job.salary_min || job.salary_max) && (
                      <div className="flex items-center gap-1 text-slate-600 col-span-2">
                        <IndianRupee className="w-3 h-3" />
                        {job.salary_min ?? '0'} - {job.salary_max ?? '∞'} LPA
                      </div>
                    )}
                  </div>

                  {job.description && (
                    <p className="text-sm text-slate-500 line-clamp-2">{job.description}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Job Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Job' : 'Create Job'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update job posting details' : 'Create a new job posting'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Job Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Senior Software Engineer"
                  data-testid="job-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Engineering"
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Bangalore, India"
                />
              </div>
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={formData.employment_type} onValueChange={(v) => setFormData({ ...formData, employment_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Min Experience (years)</Label>
                <Input
                  type="number"
                  value={formData.experience_min}
                  onChange={(e) => setFormData({ ...formData, experience_min: e.target.value })}
                  placeholder="2"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Experience (years)</Label>
                <Input
                  type="number"
                  value={formData.experience_max}
                  onChange={(e) => setFormData({ ...formData, experience_max: e.target.value })}
                  placeholder="8"
                />
              </div>
              <div className="space-y-2">
                <Label>Min Salary (₹ LPA)</Label>
                <Input
                  type="number"
                  value={formData.salary_min}
                  onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                  placeholder="10"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Salary (₹ LPA)</Label>
                <Input
                  type="number"
                  value={formData.salary_max}
                  onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                  placeholder="25"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Pipeline Template</Label>
                <Select value={formData.pipeline_template_id || 'default'} onValueChange={(v) => setFormData({ ...formData, pipeline_template_id: v === 'default' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select pipeline template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default Pipeline</SelectItem>
                    {pipelineTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Job Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the role and responsibilities..."
                  rows={4}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Requirements</Label>
                <Textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder="List required skills and qualifications..."
                  rows={4}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowDialog(false); }}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-job-btn">
              {isEditing ? 'Update Job' : 'Create Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobList;
